import axios, { AxiosError, AxiosInstance, InternalAxiosRequestConfig } from 'axios';
import { maintenanceStore } from '@/maintenance/store';
import { isMaintenanceResponse } from '@/maintenance/response';
import { reportUnauthenticated } from '@/auth/sessionExpiry';

// Create axios instance with default config
export const apiClient: AxiosInstance = axios.create({
  baseURL: import.meta.env.VITE_API_URL || '',
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
    Accept: 'application/json',
  },
  withCredentials: true, // Required for Sanctum CSRF cookies
  xsrfCookieName: 'XSRF-TOKEN', // Cookie name set by Laravel Sanctum
  xsrfHeaderName: 'X-XSRF-TOKEN', // Header name expected by Laravel
});

// Track if we've fetched the CSRF cookie
let csrfInitialized = false;

// Fetch CSRF cookie from Sanctum
async function ensureCsrfCookie(): Promise<void> {
  if (csrfInitialized) return;
  try {
    await axios.get('/sanctum/csrf-cookie', { withCredentials: true });
    csrfInitialized = true;
  } catch (error) {
    console.warn('Failed to fetch CSRF cookie:', error);
  }
}

// Helper to extract cookie value by name
function getCookie(name: string): string | null {
  const match = document.cookie.match(new RegExp('(^| )' + name + '=([^;]+)'));
  if (match) {
    // URL-decode the cookie value
    return decodeURIComponent(match[2]);
  }
  return null;
}

// Request interceptor - add CSRF token and correlation ID
apiClient.interceptors.request.use(
  async (config: InternalAxiosRequestConfig) => {
    // Ensure CSRF cookie is set before making requests
    await ensureCsrfCookie();

    // Manually set XSRF token header from cookie
    const xsrfToken = getCookie('XSRF-TOKEN');
    if (xsrfToken) {
      config.headers['X-XSRF-TOKEN'] = xsrfToken;
    }

    // Add correlation ID for request tracing
    config.headers['X-Correlation-ID'] = `req_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor - handle errors
apiClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    if (isMaintenanceResponse(error.response)) maintenanceStore.set(true);

    // A 401 is never redirected from here: it is REPORTED and AuthContext
    // decides (sweep 2026-09-15). After sign-in it signs the tab out once and
    // hands off to the portal login; the bootstrap /me probe handles its own.
    // The error still propagates to the caller either way.
    if (error.response?.status === 401) {
      reportUnauthenticated(error.config?.url);
    }

    // Enhance error with API error details
    if (error.response?.data) {
      const apiError = error.response.data as {
        success?: boolean;
        message?: string;
        redirect?: string;
        error?: { code: string; message: string };
      };

      if (apiError.error) {
        const enhancedError = new Error(apiError.error.message);
        (enhancedError as Error & { code?: string; status?: number }).code = apiError.error.code;
        (enhancedError as Error & { status?: number }).status = error.response.status;
        // Keep the ORIGINAL response reachable (2026-08-07). This branch used
        // to flatten a domain refusal to just {message, code, status} and throw
        // the rest away — so a refusal that carries structured detail could not
        // deliver it. INVOICE_DUPLICATE_SUSPECTED sends the existing invoice's
        // id/number/status/amount precisely so the page can name it, and all of
        // that was being dropped here; the page fell through to the generic
        // "changed by someone else" 409 text. Purely additive: `code` and
        // `status` are untouched, and `response` now matches the shape the
        // apiError.ts helpers already read from raw axios errors.
        (enhancedError as Error & { response?: unknown }).response = error.response;
        return Promise.reject(enhancedError);
      }
    }

    return Promise.reject(error);
  }
);

// Type-safe API methods
export const api = {
  get: <T>(url: string, config?: Parameters<typeof apiClient.get>[1]) =>
    apiClient.get<T>(url, config).then((res) => res.data),

  post: <T>(url: string, data?: unknown, config?: Parameters<typeof apiClient.post>[2]) =>
    apiClient.post<T>(url, data, config).then((res) => res.data),

  put: <T>(url: string, data?: unknown, config?: Parameters<typeof apiClient.put>[2]) =>
    apiClient.put<T>(url, data, config).then((res) => res.data),

  patch: <T>(url: string, data?: unknown, config?: Parameters<typeof apiClient.patch>[2]) =>
    apiClient.patch<T>(url, data, config).then((res) => res.data),

  delete: <T>(url: string, config?: Parameters<typeof apiClient.delete>[1]) =>
    apiClient.delete<T>(url, config).then((res) => res.data),
};
